import React, { useState, useRef, useEffect } from 'react';
import { Plus, Check, Trash2 } from 'lucide-react';
import { TabConfig } from '../hooks/useTableTabs';
import './TableTabs.css';

interface TableTabsProps {
  tabs: TabConfig[];
  activeTabId: string;
  onAddTab: () => void;
  onSwitchTab: (tabId: string) => void;
  onRenameTab: (tabId: string, newName: string) => void;
  onDeleteTab: (tabId: string) => Promise<void>;
}

const TableTabs: React.FC<TableTabsProps> = ({
  tabs,
  activeTabId,
  onAddTab,
  onSwitchTab,
  onRenameTab,
  onDeleteTab
}) => {
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingTabId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTabId]);

  const handleRenameStart = (tab: TabConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTabId(tab.id);
    setEditingName(tab.name);
  };

  const handleRenameSave = (tabId: string) => {
    if (editingName.trim()) {
      onRenameTab(tabId, editingName.trim());
    }
    setEditingTabId(null);
    setEditingName('');
  };

  const handleRenameCancel = () => {
    setEditingTabId(null);
    setEditingName('');
  };

  const handleDeleteClick = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('Delete clicked for tab:', tabId, 'Total tabs:', tabs.length);
    if (tabs.length > 1) {
      console.log('Showing delete confirmation modal');
      setShowDeleteConfirm(tabId);
    } else {
      console.log('Cannot delete - only one tab remaining');
      alert('Cannot delete the last table. You must have at least one table.');
    }
  };

  const handleDeleteConfirm = async (tabId: string) => {
    console.log('Delete confirmed for tab:', tabId);
    setIsDeleting(true);
    try {
      console.log('Calling onDeleteTab with:', tabId);
      await onDeleteTab(tabId);
      console.log('onDeleteTab called successfully');
      setShowDeleteConfirm(null);
      setIsDeleting(false);
      // If we were editing this tab, cancel editing
      if (editingTabId === tabId) {
        setEditingTabId(null);
        setEditingName('');
      }
    } catch (error) {
      console.error('Error deleting tab:', error);
      setShowDeleteConfirm(null);
      setIsDeleting(false);
      alert('Failed to delete table. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
    setIsDeleting(false);
  };


  const handleKeyDown = (e: React.KeyboardEvent, tabId: string) => {
    if (e.key === 'Enter') {
      handleRenameSave(tabId);
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  return (
    <div className="table-tabs-container">
      <div className="table-tabs-wrapper">
        <div className="table-tabs">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`table-tab ${tab.id === activeTabId ? 'active' : ''}`}
              onClick={() => tab.id !== activeTabId && onSwitchTab(tab.id)}
            >
              {editingTabId === tab.id ? (
                <div className="table-tab-edit" onClick={(e) => e.stopPropagation()}>
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => {
                      if (!isDeleting) {
                        handleRenameSave(tab.id);
                      }
                    }}
                    onKeyDown={(e) => handleKeyDown(e, tab.id)}
                    className="table-tab-edit-input"
                  />
                  <button
                    className="table-tab-edit-save"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameSave(tab.id);
                    }}
                    title="Save"
                  >
                    <Check size={12} />
                  </button>
                  <button
                    className="table-tab-edit-delete"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteClick(tab.id, e);
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    title="Delete table"
                    type="button"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ) : (
                <span 
                  className="table-tab-name" 
                  onDoubleClick={(e) => {
                    // Prevent editing the default tab (first tab with id 'default')
                    if (tab.id !== 'default') {
                      handleRenameStart(tab, e);
                    }
                  }}
                  style={tab.id === 'default' ? { cursor: 'default' } : {}}
                  title={tab.id === 'default' ? 'This tab cannot be renamed' : 'Double-click to rename'}
                >
                  {tab.name}
                </span>
              )}
            </div>
          ))}
          <button className="table-tab-add" onClick={onAddTab} title="Add new table">
            <Plus size={14} />
          </button>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-content">
              <h3>Delete Table?</h3>
              <p>Are you sure to delete the table? This will permanently delete all founders in this table.</p>
              <div className="delete-confirm-actions">
                <button
                  type="button"
                  className="delete-confirm-button delete-confirm-cancel"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                >
                  No
                </button>
                <button
                  type="button"
                  className="delete-confirm-button delete-confirm-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showDeleteConfirm) {
                      handleDeleteConfirm(showDeleteConfirm);
                    }
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableTabs;

